import * as React from "react";
import { Link, useLocation } from "react-router";
import { useGetIdentity } from "@refinedev/core";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { canAccess } from "@/providers/accessControlProvider";
import type { AdminIdentity, AdminRole } from "@/providers/types";

interface NavItem {
  label: string;
  href: string;
  resource: string;
  action: string;
  icon: React.ReactNode;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "Пользователи",
    href: "/users",
    resource: "users",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Места",
    href: "/places",
    resource: "places",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    label: "Модерация",
    href: "/moderation",
    resource: "moderation",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    label: "Операторы",
    href: "/operators",
    resource: "operators",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    label: "Журнал аудита",
    href: "/audit",
    resource: "audit",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
  },
  {
    label: "Дашборд",
    href: "/dashboard",
    resource: "dashboard",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
  },
  {
    label: "Импорт",
    href: "/import",
    resource: "import",
    action: "list",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      </svg>
    ),
  },
];

export function Sidebar(): React.JSX.Element {
  const location = useLocation();
  const { data: identity } = useGetIdentity<AdminIdentity>();
  const role = identity?.role as AdminRole | undefined;

  const isActive = (href: string): boolean =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  const visibleItems = NAV_ITEMS.filter((item) =>
    canAccess({ resource: item.resource, action: item.action, role }).can
  );

  return (
    <aside
      className="w-[240px] min-w-[240px] bg-[var(--surface)] border-r border-[var(--border)] flex flex-col fixed top-0 left-0 bottom-0 z-10"
      aria-label="Навигация администратора"
    >
      <div className="px-4 pt-5 pb-3 border-b border-[var(--border)]">
        <div className="text-[20px] font-[700] text-[var(--fg)] tracking-[-0.3px]">
          Jourio
        </div>
        <div className="text-[11px] font-[600] uppercase tracking-[0.8px] text-[var(--muted)] mt-0.5">
          Admin
        </div>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto" aria-label="Разделы">
        <ul role="list">
          {visibleItems.map((item) => {
            const active = isActive(item.href);
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  aria-label={
                    item.badge !== undefined && item.badge > 0
                      ? `${item.label}, ${item.badge} элементов требуют внимания`
                      : item.label
                  }
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-[10px] px-4 min-h-[36px] py-2 text-[13px] font-[500] border-l-[3px] transition-[background,color] duration-[120ms] no-underline",
                    active
                      ? "bg-[var(--accent-weak)] text-[var(--accent)] border-l-[var(--accent)]"
                      : "text-[var(--muted)] border-l-transparent hover:bg-[var(--surface-2)] hover:text-[var(--fg)]",
                  )}
                >
                  <span
                    className={cn(
                      "w-[18px] h-[18px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:stroke-[1.8px] [&>svg]:fill-none",
                    )}
                  >
                    {item.icon}
                  </span>
                  {item.label}
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      className="ml-auto bg-[var(--danger)] text-white text-[10px] font-[700] px-1.5 py-px rounded-[10px]"
                      aria-hidden="true"
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {identity && (
        <div className="px-4 py-[14px] border-t border-[var(--border)]">
          {identity.email && (
            <div className="text-[12px] text-[var(--fg)] font-[500] whitespace-nowrap overflow-hidden text-ellipsis">
              {identity.email}
            </div>
          )}
          {identity.role && (
            <Badge variant="default" className="mt-1">
              {identity.role}
            </Badge>
          )}
        </div>
      )}
    </aside>
  );
}
